import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowUpRight } from 'lucide-react';
import AuthContext from '../../context/App/AuthContext';
import { useContacts } from '../../api/contacts';

const SECTIONS = [
  {
    label: 'Content Admin',
    items: [
      { to: '/managehome', label: 'Home Page', sub: 'Hero copy, sections' },
      { to: '/manageabout', label: 'About Page', sub: 'Profile, bio, quote' },
      { to: '/manageblog', label: 'Blog', sub: 'Articles & drafts' },
      { to: '/manageservice', label: 'Services', sub: 'Catalogue & pricing' },
      { to: '/managecourse', label: 'Courses', sub: 'School & enrolments' },
      { to: '/managereviews', label: 'Reviews', sub: 'Client testimonials' },
    ],
  },
  {
    label: 'Administration',
    items: [
      { to: '/contactinfo', label: 'Inbox', sub: 'Client inquiries', badge: true },
      { to: '/settings', label: 'Settings', sub: 'Personal profile' },
    ],
  },
];

export default function UserProfilePanel({ isOpen, onClose }) {
  const { user, Logout } = useContext(AuthContext);
  const { data: contacts = [] } = useContacts({ enabled: isOpen });

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const unreadCount = contacts.filter((c) => !c.read).length;

  const handleLogout = (e) => {
    e.preventDefault();
    onClose();
    Logout();
  };

  const initials = user?.name ? user.name.charAt(0).toUpperCase() : 'A';

  return (
    <>
      <div
        className={`fixed inset-0 bg-ink/30 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      <aside
        className={`fixed top-0 right-0 w-[22rem] h-full bg-paper-soft border-l border-hairlineStrong z-50 transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="px-6 py-6 border-b border-hairlineStrong flex items-start justify-between">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 border border-ink flex items-center justify-center font-display text-lg">
              {initials}
            </span>
            <div>
              <p className="font-display text-lg leading-none">
                {user?.name || 'Administrator'}
              </p>
              <p className="eyebrow text-ink-faint mt-2">Admin Access</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close panel"
            className="text-ink-muted hover:text-ink transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-6 py-8 space-y-10">
          {SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="eyebrow text-ink-muted mb-5">{section.label}</p>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className="group flex items-start justify-between py-3 -mx-2 px-2 hover:bg-paper-soft-deep transition-colors"
                  >
                    <div>
                      <p className="font-display text-lg leading-tight group-hover:text-saffron transition-colors">
                        {item.label}
                      </p>
                      <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-faint mt-1">
                        {item.sub}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {item.badge && unreadCount > 0 && (
                        <span className="font-mono text-[10px] tracking-[0.12em] uppercase bg-saffron text-paper px-2 py-0.5">
                          {unreadCount} new
                        </span>
                      )}
                      <ArrowUpRight className="w-4 h-4 text-ink-faint group-hover:text-saffron group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-6 py-5 border-t border-hairlineStrong">
          <button
            onClick={handleLogout}
            className="w-full text-left font-mono text-[11px] tracking-[0.22em] uppercase text-ink-soft hover:text-saffron transition-colors py-2"
          >
            Sign out →
          </button>
        </div>
      </aside>
    </>
  );
}
